import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { User, Settings, LogOut, LayoutDashboard } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUser } from '@/contexts/UserContext';
import { toast } from 'sonner';

export const AuthButton = () => {
  const [profile, setProfile] = useState<any>(null);
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { user, isAdmin, isLoading } = useUser();

  useEffect(() => {
    // Fetch profile data if user exists
    if (user) {
      supabase
        .from('profiles')
        .select('avatar_url, full_name, first_name, last_name')
        .eq('id', user.id)
        .maybeSingle()
        .then(({ data }) => setProfile(data));
    } else {
      setProfile(null);
    }
  }, [user]);

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      toast.success(language === 'en' ? 'Signed out successfully' : 'Αποσύνδεση επιτυχής');
      navigate('/');
    } catch (error) {
      console.error('Sign out error:', error);
      toast.error(language === 'en' ? 'Error signing out' : 'Σφάλμα αποσύνδεσης');
    }
  };

  const getInitials = (name: string | undefined) => {
    if (!name) return 'U';
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserDisplayName = () => {
    if (profile?.full_name) {
      return profile.full_name;
    }
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    if (user?.user_metadata?.name) {
      return user.user_metadata.name;
    }
    if (user?.email) {
      return user.email.split('@')[0];
    }
    return language === 'en' ? 'User' : 'Χρήστης';
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-3">
        <div className="h-8 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
      </div>
    );
  }

  if (!user) {
    return (
      <Button 
        variant="outline" 
        onClick={() => navigate('/auth')}
        className="opacity-100 visible bg-background border border-border hover:bg-accent text-foreground"
      >
        {language === 'en' ? 'Login' : 'Σύνδεση'}
      </Button>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <Button 
        variant="default" 
        onClick={() => navigate('/dashboard')}
      >
        <LayoutDashboard className="h-4 w-4 mr-2" />
        {language === 'en' ? 'Dashboard' : 'Πίνακας Ελέγχου'}
      </Button>
      
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="relative h-10 w-10 rounded-full p-0">
            <Avatar className="h-10 w-10 border-2 border-border">
              <AvatarImage src={profile?.avatar_url || user?.user_metadata?.avatar_url} />
              <AvatarFallback className="bg-muted text-foreground text-sm font-medium">
                {getInitials(getUserDisplayName())}
              </AvatarFallback>
            </Avatar>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 bg-background border border-border shadow-lg z-[60]" align="end" forceMount>
          <DropdownMenuLabel className="font-normal">
            <div className="flex flex-col space-y-1">
              <p className="text-sm font-medium leading-none">
                {getUserDisplayName()}
              </p>
              <p className="text-xs leading-none text-muted-foreground">
                {user?.email}
              </p>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => navigate('/dashboard')}>
            <LayoutDashboard className="mr-2 h-4 w-4" />
            <span>{language === 'en' ? 'Dashboard' : 'Πίνακας Ελέγχου'}</span>
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => navigate(isAdmin ? '/settings' : '/profile')}>
            <Settings className="mr-2 h-4 w-4" />
            <span>{language === 'en' ? (isAdmin ? 'Settings' : 'Profile') : (isAdmin ? 'Ρυθμίσεις' : 'Προφίλ')}</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleSignOut}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>{language === 'en' ? 'Log out' : 'Αποσύνδεση'}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};